import { makeStyles, Typography } from '@material-ui/core';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  /*
   * Visual marker between sections of an admin-authored content page.
   * Renders as a heading row with an accent stripe so it stands out
   * from `markdown` headings (which a long page often contains many of).
   * The accent uses Bosch Blue 50 — matches the sidebar/banner brand
   * color so the divider reads as "first-class structural element"
   * rather than ad-hoc decoration.
   */
  root: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderTop: `2px solid ${boschBlue[50]}`,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: boschGray[15],
    lineHeight: 1.2,
    marginTop: theme.spacing(1),
  },
  subtitle: {
    fontSize: '0.95rem',
    color: boschGray[40],
    marginTop: theme.spacing(0.5),
    lineHeight: 1.45,
    maxWidth: '80ch',
  },
}));

export type SectionHeaderProps = {
  title: string;
  subtitle?: string;
};

/**
 * Layout-only widget that lets admins break a long content page into
 * named sections without resorting to a Markdown `## heading` (which
 * mixes structural anchors with prose styles).
 *
 * Intentionally simple — title + optional subtitle. Anything more
 * elaborate (icon, badges, action button) belongs in a separate
 * widget so this one stays a pure separator.
 */
export const SectionHeader = ({ title, subtitle }: SectionHeaderProps) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <Typography variant="h2" className={classes.title}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body1" className={classes.subtitle}>
          {subtitle}
        </Typography>
      )}
    </div>
  );
};
